import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Video, Check, Wifi, FileText, Clock, ShieldCheck, Headphones, ChevronDown, MapPin, Vote, Scale, Users } from "lucide-react";
import { PageHero } from "@/pages/Agence";

// Picto « coup de pinceau » de la charte Léa Syndic.
const LeaSyndicMark = ({ className = "" }) => (
  <svg viewBox="0 0 108.52 79.65" className={className} fill="#69B799" aria-hidden="true">
    <path d="M105.23,32.61c7.06-13.69,1.71-25-6.26-29.73a24.2,24.2,0,0,0-32.31,9.06c-3.11,4.63-30.43,44.18-57.9,55.31l-.06,0c-.88.35-1.75.77-2.62,1.06-5.48,1.83-6.3,4.54-6,6.5.41,2.92,3.16,4.51,8.17,4.73,1.3.07,2.63.09,4,.09A133,133,0,0,0,60.87,69.59C75.53,63.54,95.11,52.23,105.23,32.61Z" />
  </svg>
);

// Picto « flèche » de la charte, colonne Copropriété.
const LeaCoproMark = ({ className = "" }) => (
  <svg viewBox="0 0 103.35 130.72" className={className} fill="#DE7268" aria-hidden="true">
    <path d="M97.86,10.65,65,.14a2.9,2.9,0,0,0-3.24,4.44L75.25,23.43c-.85.29-1.7.58-2.56.91A137.55,137.55,0,0,0,29.4,52C17.48,63.3,2.75,81.45.21,104.12c-1.75,15.83,7.59,24.78,17,26.4a25,25,0,0,0,27.85-20.68c1.3-5.61,13.07-53.88,35.49-74.78l0-.05c.58-.54,1.15-1.1,1.73-1.63l15.73,22a2.89,2.89,0,0,0,4,.68,2.93,2.93,0,0,0,1.22-2.36V18.15A7.87,7.87,0,0,0,97.86,10.65Z" />
  </svg>
);

// Picto bicolore de la charte (flèche corail + coup de pinceau vert).
const LeaOptionMark = ({ className = "" }) => (
  <svg viewBox="0 0 186.8 214.07" className={className} aria-hidden="true">
    <path fill="#DE7268" d="M182.41,31.22,143.23.89a4.17,4.17,0,0,0-6.56,4.41l8.91,32.05c-1.29,0-2.59,0-3.91,0A197.39,197.39,0,0,0,69.59,53.47c-21.63,9.33-50.44,26.61-65,56-10.15,20.51-1.92,37.19,10,44a36,36,0,0,0,47.78-14.23c4.51-6.94,44.14-66.34,84.67-83.53l.1,0c1-.44,2.1-.94,3.14-1.36l10.42,37.45a4.17,4.17,0,0,0,7.93.31l17.48-48A11.32,11.32,0,0,0,182.41,31.22Z" />
    <path fill="#69B799" d="M164.57,143.18c10.64-20.63,2.57-37.68-9.43-44.8A36.47,36.47,0,0,0,106.45,112c-4.7,7-45.86,66.57-87.26,83.34l-.1,0c-1.31.53-2.63,1.15-3.94,1.6-8.26,2.75-9.5,6.84-9.11,9.79.61,4.4,4.75,6.79,12.3,7.13,2,.1,4,.14,6,.14A200.32,200.32,0,0,0,97.72,198.9C119.82,189.79,149.32,172.74,164.57,143.18Z" />
  </svg>
);

const PILLARS = [
  {
    icon: ShieldCheck,
    title: "Un vrai syndic professionnel",
    text: "Sans les contraintes du modèle traditionnel. Sans les coûts de structure des grands cabinets (loyers, secrétariat, papier). Votre budget va dans le conseil, le suivi et la valorisation de votre immeuble — pas dans les frais de structure.",
  },
  {
    icon: Wifi,
    title: "Votre espace copropriétaire 24h/24",
    text: "Documents (règlement, PV, contrats, diagnostics), comptes et appels de fonds en temps réel, demandes d'intervention, votes : tout depuis un accès personnel sécurisé.",
  },
  {
    icon: Video,
    title: "Assemblées générales en visioconférence",
    text: "Plus besoin de bloquer une soirée ou de vous déplacer. AG juridiquement valides, courtes et efficaces, accessibles depuis chez vous via un lien sécurisé. Vous participez, vous votez, le PV est rédigé en séance.",
  },
  {
    icon: Headphones,
    title: "Un gestionnaire dédié",
    text: "Vous savez qui vous appelez. Réponse sous 48h ouvrées, transparence totale sur les comptes et les décisions, zéro paperasse — tout est dématérialisé, adapté pour les copropriétés de moins de 20 lots (au-delà sur mesure).",
  },
];

const SCOPE = [
  {
    h: "ASSEMBLÉE GÉNÉRALE",
    rows: [
      { label: "Préparation de l’assemblée générale", values: [true, false, true, false] },
      { label: "Convocation à l’assemblée générale", values: [true, false, false, false] },
      { label: "Tenue de l’assemblée générale en visio", values: [true, false, false, false] },
      { label: "Diffusion du PV d’assemblée générale", values: [true, false, false, false] },
    ],
  },
  {
    h: "CONSEIL SYNDICAL",
    rows: [
      { label: "Mise à disposition et communication au conseil syndical de toutes pièces ou documents se rapportant à la gestion du syndicat ou des lots gérés", values: [true, false, false, false] },
      { label: "Recueil des avis écrits du conseil syndical lorsque sa consultation est obligatoire", values: [true, false, true, false] },
    ],
  },
  {
    h: "GESTION DES OPÉRATIONS FINANCIÈRES ET COMPTABILITÉ GÉNÉRALE DE LA COPROPRIÉTÉ",
    rows: [
      { label: "Comptabilité du syndicat - Etablissement des comptes de gestion et des annexes", values: [true, false, false, false] },
      { label: "Etablissement du budget prévisionnel", values: [true, false, true, false] },
      { label: "Présentation des comptes en conformité avec la réglementation en vigueur", values: [true, false, false, false] },
      { label: "Relevé des éventuels compteurs", values: [false, false, true, false] },
      { label: "Comptes bancaires - Ouverture et gestion", values: [true, false, false, false] },
      { label: "Comptabilité séparée de chaque propriétaire - Tenue des comptes de chaque copropriétaire", values: [true, false, false, false] },
      { label: "Appel des provisions sur budget prévisionnel", values: [true, false, false, false] },
      { label: "Imputations des consommations individuelles de fluide ou d’énergie", values: [true, false, false, false] },
      { label: "Reconstitution des consommations, forfaits et régularisations sur compteurs en l’absence de relevé", values: [true, false, false, false] },
      { label: "Appels sur régularisation de charge", values: [true, false, false, false] },
      { label: "Appels des cotisations du fond de travaux", values: [true, false, false, false] },
      { label: "Vérification des factures des fournisseurs et prestataires", values: [true, false, true, false] },
      { label: "Paiement des factures des fournisseurs et prestataires", values: [true, false, false, false] },
      { label: "Recouvrement des créances auprès des tiers", values: [true, false, false, false] },
      { label: "Calcul des intérêts légaux au profit du syndicat", values: [true, false, false, false] },
      { label: "Attestation de TVA aux fournisseurs et prestataires", values: [true, false, false, false] },
      { label: "Remise au syndic successeur - Remise de l’ensemble des éléments réglementaires", values: [true, false, false, false] },
    ],
  },
  {
    h: "ADMINISTRATION ET GESTION DE LA COPROPRIÉTÉ EN CONFORMITÉ AVEC LE RÈGLEMENT DE COPROPRIÉTÉ",
    rows: [
      { label: "Immatriculation du syndicat - Immatriculation de l’immeuble", values: [true, false, false, false] },
      { label: "Mise à jour du registre d’immatriculation", values: [true, false, false, false] },
      { label: "Elaboration et mise à jour de la fiche synthétique de copropriété", values: [true, false, false, false] },
      { label: "Gestion de tous les audits, diagnostics et dossiers obligatoires", values: [true, false, false, false] },
      { label: "Présence lors de la réalisation des audits", values: [false, false, true, false] },
      { label: "Etablissement et mise à jour du carnet d’entretien conformément au décret n°2001-477 du 30 Mai 2001", values: [true, false, false, false] },
      { label: "Etablissement et mise à jour de la liste des copropriétaires", values: [true, false, false, false] },
      { label: "Détention et conservation des archives", values: [true, false, false, false] },
      { label: "Transmission des archives", values: [true, false, false, false] },
      { label: "Mise à disposition d’un accès en ligne sécurisé aux documents dématérialisés", values: [true, false, false, false] },
      { label: "Présence lors de rendez-vous sur site", values: [false, false, true, false] },
      { label: "Gestion des travaux d’entretien courants et de maintenance", values: [true, false, false, false] },
      { label: "Accueil des prestataires sur site", values: [false, false, true, false] },
      { label: "Vérification des prestations", values: [false, false, true, false] },
      { label: "Participation aux réunions de chantiers et réception de travaux", values: [false, false, true, false] },
      { label: "Signature de la réception des travaux", values: [true, false, false, false] },
      { label: "Vérifications périodiques imposées par les réglementations en vigueur sur les éléments d’équipement communs", values: [true, false, true, false] },
      { label: "Négociations, passations, suivi des marchés des prestataires et gestion des contrats à l’échéance", values: [true, false, true, false] },
      { label: "Mise en concurrence et analyse complète des contrats : appels, étude et synthèse des offres", values: [true, false, true, false] },
    ],
  },
  {
    h: "ASSURANCES",
    rows: [
      { label: "Souscription des polices d’assurance", values: [true, false, false, false] },
      { label: "Déclaration des sinistres - Concernant les parties communes ou les parties privatives lorsque le dommage à sa source dans les parties communes", values: [true, false, false, false] },
      { label: "Participation aux réunions d’expertise", values: [false, false, true, false] },
    ],
  },
  {
    h: "GESTION DU PERSONNEL",
    rows: [
      { label: "Prestations de gestion de personnel sur devis.", values: [false, false, false, false] },
    ],
  },
];

export default function SyndicEnLigne() {
  const [openScope, setOpenScope] = useState(0);
  return (
    <div data-testid="page-syndic-en-ligne">
      <PageHero
        overline="Nos métiers — Syndic en ligne"
        title={(
          <>
            Copropriété en ligne
            <br />
            Nouvelle Génération
          </>
        )}
        subtitle="Un syndic professionnel, réactif et 100% accessible depuis chez vous. La rigueur d'un cabinet, la simplicité du numérique."
      />

      {/* Manifesto */}
      <section className="py-16 sm:py-20">
        <div className="max-w-[960px] mx-auto px-4 sm:px-6 lg:px-8 text-left">
          <p className="text-[#475569] text-2xl lg:text-3xl leading-relaxed">
            Vous avez adopté la banque en ligne, vous avez adopté l'assurance en ligne,
            pourquoi ne pas adopter votre <span className="text-[#0F172A] font-medium">syndic de copropriété en ligne</span>&nbsp;?
          </p>
          <p className="text-[#475569] text-2xl lg:text-3xl leading-relaxed mt-4">
            Une gestion moderne, transparente et accessible à tout moment.
          </p>
        </div>
      </section>

      {/* Scope */}
      <section className="py-16 sm:py-20 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mb-14">
            <span className="font-overline-lg text-[#B8935A]">Qui fait quoi</span>
            <h2 className="font-serif text-4xl text-[#0F172A] mt-4 leading-tight">
              Une prestation complète, sans zone d'ombre.
            </h2>
            <p className="text-[#64748B] mt-6 leading-relaxed">
              Notre mission couvre l'ensemble des obligations légales et techniques d'un syndic,
              en collaboration étroite avec votre conseil syndical.
            </p>
          </div>
          <div className="space-y-3">
            {SCOPE.map((s, i) => (
              <details
                key={s.h}
                className="group border border-[#E2E8F0] bg-white p-6 cursor-pointer"
                data-testid={`scope-accordion-${i}`}
                open={openScope === i}
              >
                <summary
                  className="flex items-start justify-between gap-4 list-none"
                  onClick={(e) => {
                    e.preventDefault();
                    setOpenScope(openScope === i ? null : i);
                  }}
                >
                  <h3 className="font-serif text-xl text-[#0F172A] flex-1">{s.h}</h3>
                  <span className="w-9 h-9 rounded-full border-2 border-[#B8935A] text-[#B8935A] flex items-center justify-center shrink-0 transition-all group-open:rotate-180 group-open:bg-[#B8935A] group-open:text-white">
                    <ChevronDown size={20} strokeWidth={2.5} />
                  </span>
                </summary>
                <div className="mt-4 overflow-x-auto">
                  <table className="w-full border-collapse text-left">
                    <thead>
                      <tr className="bg-[#0F172A] text-white">
                        <th className="py-3 px-2 text-left text-[10px] uppercase tracking-wide">Service</th>
                        <th className="py-3 px-2 text-center text-[10px] uppercase tracking-wide">
                          <span className="inline-flex items-center justify-center gap-1.5">
                            <LeaSyndicMark className="w-4 shrink-0" />
                            Cabinet GPI
                          </span>
                        </th>
                        <th className="py-3 px-2 text-center text-[10px] uppercase tracking-wide"></th>
                        <th className="py-3 px-2 text-center text-[10px] uppercase tracking-wide">
                          <span className="inline-flex items-center justify-center gap-1.5">
                            <LeaCoproMark className="h-4 w-auto shrink-0" />
                            Copropriété
                          </span>
                        </th>
                        <th className="py-3 px-2 text-center text-[10px] uppercase tracking-wide"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {s.rows.map((row, rowIndex) => (
                        <tr
                          key={`${s.h}-${row.label}`}
                          className={rowIndex % 2 === 0 ? "bg-white" : "bg-slate-50"}
                        >
                          <td className="py-3 pr-4 text-sm text-[#475569] align-top leading-relaxed">
                            {/* Picto bicolore réservé aux prestations partagées
                                entre le Cabinet GPI et la copropriété. */}
                            <span className="flex items-start gap-2">
                              {row.values[0] && row.values[2] && (
                                <LeaOptionMark className="h-4 w-auto shrink-0 mt-0.5" />
                              )}
                              {row.label}
                            </span>
                          </td>
                          <td className="py-3 px-2 text-center text-sm text-[#475569]">
                            {row.values[0] ? <span className="inline-block text-[#B8935A] font-bold">●</span> : ""}
                          </td>
                          <td className="py-3 px-2 text-center text-sm text-[#475569]">
                            {row.values[1] ? <span className="inline-block text-[#B8935A] font-bold">●</span> : ""}
                          </td>
                          <td className="py-3 px-2 text-center text-sm text-[#475569]">
                            {row.values[2] ? <span className="inline-block text-[#B8935A] font-bold">●</span> : ""}
                          </td>
                          <td className="py-3 px-2 text-center text-sm text-[#475569]">
                            {row.values[3] ? <span className="inline-block text-[#B8935A] font-bold">●</span> : ""}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </details>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 bg-[#0F172A] text-white border-b border-[#D4B584]/20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-5 text-center">
            <p className="font-overline-lg text-[#D4B584]">GPI SYNDIC</p>
            <p className="flex items-center justify-center gap-3 text-[#E2E8F0] text-lg leading-relaxed">
              <MapPin size={22} className="text-[#D4B584] shrink-0" aria-hidden="true" />
              Adapté à votre mode de vote en temps réel. (Vous assistez à l'AG d'où vous voulez.)
            </p>
            <p className="flex items-center justify-center gap-3 text-[#E2E8F0] text-lg leading-relaxed">
              <Vote size={22} className="text-[#D4B584] shrink-0" aria-hidden="true" />
              Vote en temps réel (Pas de problèmes de majorité).
            </p>
            <p className="flex items-center justify-center gap-3 text-[#E2E8F0] text-lg leading-relaxed">
              <Scale size={22} className="text-[#D4B584] shrink-0" aria-hidden="true" />
              Conforme à la loi ELAN (Sans problème de contestation possible de la majorité.)
            </p>
            <p className="flex items-center justify-center gap-3 text-[#E2E8F0] text-lg leading-relaxed">
              <Users size={22} className="text-[#D4B584] shrink-0" aria-hidden="true" />
              La visioconférence au service d'une assemblée générale plus efficace, plus participative.
            </p>
          </div>
        </div>
      </section>

      {/* 4 Pillars */}
      <section className="py-16 bg-[#EFE8DC]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-6 lg:gap-8">
            {PILLARS.map((p, i) => (
              <div key={p.title} className="bg-white p-8 lg:p-10 border border-white">
                <div className="flex items-start justify-between">
                  <p.icon size={28} strokeWidth={1.4} className="text-[#B8935A]" />
                  <span className="font-overline text-[#94A3B8]">0{i + 1}</span>
                </div>
                <h3 className="font-serif text-2xl text-[#0F172A] mt-6">{p.title}</h3>
                <p className="text-[#64748B] mt-3 leading-relaxed">{p.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* AG Visio CTA Banner */}
      <section className="bg-[#0F172A] text-white py-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 grid md:grid-cols-2 gap-10 items-center">
          <div>
            <span className="font-overline-lg text-[#D4B584]">Service phare</span>
            <h2 className="font-serif text-4xl lg:text-5xl mt-4 leading-tight">
              Assemblées générales <span className="italic">en visioconférence</span>
            </h2>
          </div>
          <div className="flex md:justify-end">
            <Link
              to="/metiers/ag-visioconference"
              data-testid="link-ag-visio"
              className="inline-flex items-center gap-3 px-8 py-4 border border-[#D4B584] text-[#D4B584] hover:bg-[#D4B584] hover:text-[#0F172A] transition-all duration-300 text-sm tracking-wide"
            >
              <Video size={16} />
              En savoir plus sur les AG en visio
              <ArrowUpRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      {/* For who */}
      <section className="py-16 border-t border-[#E2E8F0]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 grid sm:grid-cols-3 gap-8">
          <div className="flex items-start gap-3">
            <FileText size={20} className="text-[#B8935A] shrink-0 mt-1" />
            <div>
              <div className="font-serif text-lg text-[#0F172A]">Un espace personnel en ligne</div>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <Clock size={20} className="text-[#B8935A] shrink-0 mt-1" />
            <div>
              <div className="font-serif text-lg text-[#0F172A]">Maîtrise de votre budget</div>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <ShieldCheck size={20} className="text-[#B8935A] shrink-0 mt-1" />
            <div>
              <div className="font-serif text-lg text-[#0F172A]">Un référent dédié</div>
            </div>
          </div>
        </div>
      </section>

      {/* Visuel tarif */}
      <section className="py-16 border-t border-[#E2E8F0]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <img
            src="/images/tarif-syndic.jpg"
            alt="À partir de 12 € par mois et par lot — immatriculation comprise, étude personnalisée, audit administratif et financier gratuit"
            className="w-full h-auto rounded-2xl"
            loading="lazy"
          />
        </div>
      </section>

      {/* Tarif */}
      <section className="py-2 sm:py-3 border-t border-[#E2E8F0] max-w-3xl mx-auto">
        <div className="max-w-xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Link
            to="/contact"
            data-testid="page-syndic-en-ligne-audit-btn"
            className="gpi-btn-primary"
          >
            Demander mon étude
            <ArrowUpRight size={14} />
          </Link>
        </div>
      </section>

    </div>
  );
}
